import React, { useState, FC } from "react";
import styled from "styled-components";
import cx from "classnames";
import { FulfillmentItemProduct } from "./Product";
import { colors } from "@pepdirect/ui/variables";
import { renderTrackingNumber } from "components/Tracking/helpers";
import { SubmittedOrderForOrderConfirmationQuery } from "services/graphql/generated";
import st from "./item.module.scss";

const StyledFulfillmentItem = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius || "10px"};
  background-color: ${({ theme }) =>
    theme.page?.backgroundColor ? "white" : "#f1f1f1"};
  padding: 10px 20px;

  & + & {
    margin-top: 10px;
  }

  @media print {
    background-color: white;
    border: 1px solid ${colors.lightestGray};
    border-radius: 0;
  }
`;

export interface FullfillmentItemProps {
  fulfillment: NonNullable<
    NonNullable<
      SubmittedOrderForOrderConfirmationQuery["submittedOrder"]
    >["fulfillments"]
  >[number];
}

export const FulfillmentItem: FC<FullfillmentItemProps> = ({ fulfillment }) => {
  const [visible, setVisible] = useState(false);
  const toggleVisibility = () => setVisible(!visible);

  const chevronClasses = cx(st.chevron, { [st.up]: visible });
  const contentClasses = cx(st.content, { [st.visible]: visible });

  const mappedItems = fulfillment.items.filter(({ item }) => item);

  const notMappedAmount =
    fulfillment.items
      .filter(({ item }) => !item)
      .map(({ quantity }) => quantity)
      .reduce((acc, curr) => (acc || 0) + (curr || 0), 0) || 0;

  const trackingNumber = fulfillment.trackingNumber || "";
  const trackingUrl = fulfillment?.trackingNumbers[0]?.trackingUrl || "";
  const status = fulfillment?.trackingNumbers[0]?.status || "";

  if (
    fulfillment.status === "NoChange" ||
    fulfillment.trackingNumbers?.length === 0
  ) {
    return null;
  }

  const renderStatusContainer = () => {
    switch (fulfillment.status) {
      case "Complete":
        return (
          <div className={st.info}>
            <div className={st.infoDetail} style={{ flexBasis: "40%" }}>
              <div className={st.infoDetailTitle}>Status:</div>
              <div className={st.statusText}>{status.replace(/_/g, " ")}</div>
            </div>
            <div className={st.infoDetail} style={{ flexBasis: "60%" }}>
              <div className={st.infoDetailTitle}>Tracking:</div>
              {renderTrackingNumber(trackingNumber, trackingUrl || "")}
            </div>
          </div>
        );
      case "Canceled":
        return (
          <div className={st.info}>
            <div className={st.infoDetail} style={{ flexBasis: "40%" }}>
              <div className={st.infoDetailTitle}>Status:</div>
              <div className={cx(st.statusText, st.canceled)}>Canceled</div>
            </div>
          </div>
        );
      case "Unknown":
        return <div />;
    }
  };

  return (
    <StyledFulfillmentItem>
      <div className={st.title}>
        {renderStatusContainer()}
        <div
          className={st.button}
          onClick={toggleVisibility}
          onKeyPress={toggleVisibility}
          role="button"
          tabIndex={0}
        >
          <i className={chevronClasses} />
        </div>
      </div>
      <div className={contentClasses}>
        {mappedItems.map((item) => (
          <FulfillmentItemProduct item={item} key={item.item?.id} />
        ))}
        {notMappedAmount > 0 && (
          <span>
            {mappedItems.length > 0
              ? `and ${notMappedAmount} more piece`
              : `${notMappedAmount} piece`}
            {notMappedAmount > 1 && notMappedAmount !== 0 ? "s" : ""}
          </span>
        )}
      </div>
    </StyledFulfillmentItem>
  );
};
